import { Fragment, useState } from 'react';
import { ErroreApi } from '../api/cliente.js';
import {
  type EsitoEsecuzione,
  type EsitoScarico,
  type FlussoBc,
  daQuanto,
  inRitardo,
  useEsecuzioniBc,
  useFlussiBc,
  useFlussiVettori,
  useCambiaOrario,
  useLanciaFlusso,
  useLanciaImport,
  useOrariImport,
  useRigheEsecuzione,
  useScaricaVettore,
} from '../api/integrazioni.js';
import { dataOra } from '../formati.js';
import { locale, t } from '../i18n/lingua.js';
import { usePuoAprire } from '../permessi.js';
import { AggiornaStatiBc } from './AggiornaStatiBc.js';

/**
 * Le integrazioni con Business Central.
 *
 * La pagina risponde a una domanda sola: **gli import girano?** Nel v2 non
 * c'è modo di chiederlo — e infatti gli articoli della Inc non arrivano dal
 * 31 dicembre 2023 e nessuno se n'era accorto, perché non c'era un posto
 * dove accorgersene.
 *
 * Lanciare a mano si può e ogni tanto serve, ma è l'eccezione: il modo
 * normale è che girino da soli e che qui si venga a guardare quando un
 * numero non torna.
 */

/**
 * Le cadenze che si possono scegliere. Non è un campo libero apposta: fra
 * «ogni 7 minuti» e «ogni 5» non cambia niente per chi lavora, e un campo
 * libero invita a scriverci 1.
 */
const CADENZE = [5, 15, 30, 60, 120, 180, 360, 720, 1440] as const;

function cadenza(minuti: number): string {
  if (minuti < 60) return `${minuti} min`;
  if (minuti === 1440) return t('un giorno');
  const ore = minuti / 60;
  return ore === 1 ? t('un’ora') : `${ore} ${t('ore')}`;
}

const STATO: Record<string, { testo: string; classe: string }> = {
  running: { testo: t('in corso'), classe: 'badge-warn' },
  succeeded: { testo: t('riuscito'), classe: 'badge-ok' },
  failed: { testo: t('fallito'), classe: 'badge-alert' },
};

export function Integrazioni() {
  const flussi = useFlussiBc();
  const esecuzioni = useEsecuzioniBc();
  const lancia = useLanciaImport();
  const [apertaId, setApertaId] = useState<number | null>(null);
  const righe = useRigheEsecuzione(apertaId);
  const [ultimo, setUltimo] = useState<EsitoEsecuzione | null>(null);
  const vettori = useFlussiVettori();
  const scarica = useScaricaVettore();
  const [ultimoScarico, setUltimoScarico] = useState<EsitoScarico | null>(null);
  const orari = useOrariImport();
  const cambiaOrario = useCambiaOrario();
  const lanciaFlusso = useLanciaFlusso();
  // accendere, spegnere e cambiare ogni quanto è dei Parametri: il contabile
  // vede gli orari e lancia a mano, ma non li cambia
  const cambiaOrari = usePuoAprire()('settings');

  const errore = lancia.error instanceof ErroreApi ? lancia.error.message : null;

  const avvia = (flusso: FlussoBc, prova: boolean) =>
    lancia.mutate({ stream: flusso.key, dryRun: prova }, { onSuccess: setUltimo });

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('Integrazioni')}</h1>
          <div className="page-sub">
            {t('Articoli, clienti e destinazioni arrivano da Business Central. Qui si vede se arrivano davvero.')}
          </div>
        </div>
      </div>

      {errore && (
        <div className="avviso" role="alert" style={{ margin: '0 20px 12px' }}>
          <span className="avviso-segno">▲</span>
          <span>{errore}</span>
        </div>
      )}

      {ultimo && (
        <div
          className={ultimo.status === 'failed' ? 'avviso' : 'nota-prezzo'}
          role="status"
          style={{ margin: '0 20px 12px' }}
        >
          {ultimo.status === 'failed' && <span className="avviso-segno">▲</span>}
          <span>
            {ultimo.status === 'failed' ? (
              <>{t('Import non riuscito: {errore}', { errore: ultimo.error ?? '' })}</>
            ) : (
              <>
                {t('Lette {lette} righe: {create} create, {aggiornate} aggiornate, {apposto} già a posto', {
                  lette: ultimo.rowsRead.toLocaleString(locale()),
                  create: ultimo.created,
                  aggiornate: ultimo.updated,
                  apposto: ultimo.skipped,
                })}
                {ultimo.rowsRejected > 0 && t(', {n} scartate', { n: ultimo.rowsRejected })}.
                {ultimo.cursor === null && ` ${t('(prova a vuoto: non è stato scritto niente)')}`}
              </>
            )}
          </span>
        </div>
      )}

      <div className="grid grid-2" style={{ margin: '14px 20px' }}>
        {(flussi.data?.streams ?? []).map((f) => (
          <div className="card" key={f.key}>
            <div className="card-head">
              <span className="card-title">{f.name}</span>
              {inRitardo(f) && (
                <span className="badge badge-warn" style={{ marginLeft: 8 }}>
                  {t('da guardare')}
                </span>
              )}
            </div>
            <div className="card-body">
              <p className="dim" style={{ marginTop: 0, fontSize: 13 }}>
                {f.description}
              </p>

              <div className="fields">
                <div className="field">
                  <label>{t('Ultima volta andata bene')}</label>
                  <div className="readonly">
                    {f.lastSuccessAt ? (
                      <>
                        {daQuanto(f.lastSuccessAt)}
                        <span className="dim num" style={{ marginLeft: 8, fontSize: 12 }}>
                          {dataOra(f.lastSuccessAt)}
                        </span>
                      </>
                    ) : (
                      <span className="dim">{t('mai')}</span>
                    )}
                  </div>
                </div>
                <div className="field">
                  <label>{t('Letto fino a')}</label>
                  <div className="readonly">
                    {f.cursor ? (
                      <span className="num">{dataOra(f.cursor)}</span>
                    ) : (
                      <span className="dim">{t('niente ancora')}</span>
                    )}
                  </div>
                </div>
              </div>

              {f.lastFailureAt && (
                <p className="dim" style={{ fontSize: 12 }}>
                  {t('Ultimo tentativo fallito {quando}.', { quando: daQuanto(f.lastFailureAt) })}
                </p>
              )}

              <div className="row" style={{ gap: 6, marginTop: 10 }}>
                <button
                  className="btn"
                  disabled={lancia.isPending}
                  onClick={() => avvia(f, true)}
                  title={t('Legge da Business Central e dice cosa cambierebbe, senza scrivere')}
                >
                  {t('Prova a vuoto')}
                </button>
                <button
                  className="btn btn-primary"
                  disabled={lancia.isPending}
                  onClick={() => avvia(f, false)}
                >
                  {lancia.isPending ? t('Leggo…') : t('Importa adesso')}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {ultimoScarico && (
        <div
          className={ultimoScarico.status === 'failed' ? 'avviso' : 'nota-prezzo'}
          role="status"
          style={{ margin: '0 20px 12px' }}
        >
          {ultimoScarico.status === 'failed' && <span className="avviso-segno">▲</span>}
          <span>
            {ultimoScarico.status === 'failed' ? (
              <>{t('Scarico non riuscito: {errore}', { errore: ultimoScarico.error ?? '' })}</>
            ) : ultimoScarico.read === 0 ? (
              t('Niente di nuovo da scaricare.')
            ) : (
              <>
                {t('{lette} documenti: {applicati} applicati, {visti} già visti', {
                  lette: ultimoScarico.read,
                  applicati: ultimoScarico.applied,
                  visti: ultimoScarico.skipped,
                })}
                {ultimoScarico.waiting > 0 && t(', {n} in attesa del loro ordine', { n: ultimoScarico.waiting })}
                {ultimoScarico.rejected > 0 && t(', {n} scartati', { n: ultimoScarico.rejected })}
                {ultimoScarico.alerts > 0 && (
                  <>
                    .{' '}
                    {ultimoScarico.alerts === 1
                      ? t('1 avviso di arrivo spostato')
                      : t('{n} avvisi di arrivo spostato', { n: ultimoScarico.alerts })}
                    {ultimoScarico.notified > 0 && t(', {n} notificati', { n: ultimoScarico.notified })}
                  </>
                )}
                .
                {ultimoScarico.bcSent + ultimoScarico.bcProblems > 0 && (
                  <>
                    {' '}
                    {t('Aggiornati su Business Central: {n} ordini', { n: ultimoScarico.bcSent })}
                    {ultimoScarico.bcProblems > 0 &&
                      t(', {n} con problemi (si vedono nelle righe dell’esecuzione)', {
                        n: ultimoScarico.bcProblems,
                      })}
                    .
                  </>
                )}
              </>
            )}
          </span>
        </div>
      )}

      {/* i vettori stanno accanto a Business Central perché la domanda è la
          stessa: girano? */}
      <div className="grid grid-2" style={{ margin: '0 20px 14px' }}>
        {(vettori.data?.streams ?? []).map((v) => (
          <div className="card" key={v.key}>
            <div className="card-head">
              <span className="card-title">{v.name}</span>
            </div>
            <div className="card-body">
              <p className="dim" style={{ marginTop: 0, fontSize: 13 }}>
                {v.description}
              </p>
              <div className="fields">
                <div className="field span-2">
                  <label>{t('Legge da')}</label>
                  <div className="readonly num" style={{ fontSize: 12 }}>
                    {v.source}
                  </div>
                </div>
              </div>
              <div className="row" style={{ gap: 6, marginTop: 10 }}>
                <button
                  className="btn btn-primary"
                  disabled={scarica.isPending}
                  onClick={() => scarica.mutate(v.key, { onSuccess: setUltimoScarico })}
                >
                  {scarica.isPending ? t('Scarico…') : t('Scarica adesso')}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* i conteggi servono a fare la prova che chiede il piano: confrontarli
          con quelli di Business Central */}
      {flussi.data && (
        <div className="statebar" style={{ margin: '0 20px' }}>
          <span className="state-tab">
            {t('Articoli in archivio')}
            <b>{(flussi.data.counts.bc_articles ?? 0).toLocaleString('it-IT')}</b>
          </span>
          <span className="state-tab">
            {t('Clienti di questa azienda')}
            <b>{(flussi.data.counts.bc_customers ?? 0).toLocaleString('it-IT')}</b>
          </span>
        </div>
      )}

      <AggiornaStatiBc />

      {/* Gli orari. Sta prima delle esecuzioni perché la domanda «gira da
          solo?» viene prima di «com'è andata l'ultima volta». */}
      <div className="card" style={{ margin: '14px 20px' }}>
        <div className="card-head">
          <span className="card-title">{t('Da soli, a orario')}</span>
          {orari.data && !orari.data.clockRunning && (
            <span className="badge badge-alert" style={{ marginLeft: 8 }}>
              {t('orologio spento')}
            </span>
          )}
        </div>

        <div className="card-body" style={{ paddingBottom: 0 }}>
          {orari.data && orari.data.alertRecipients === 0 && (
            <div className="avviso" role="status" style={{ marginBottom: 12 }}>
              <span className="avviso-segno">▲</span>
              <span>
                {t('Nessuno riceverà gli avvisi: se un import si rompe non lo saprà nessuno. Gli indirizzi si mettono in Parametri.')}
              </span>
            </div>
          )}
          {orari.data && !orari.data.clockRunning && (
            <div className="nota-prezzo" style={{ marginBottom: 12 }}>
              {t('Su questo servizio l’orologio non è acceso: gli orari qui sotto si possono impostare, ma niente parte da solo.')}
            </div>
          )}
        </div>

        <div className="table-wrap">
          <table className="tabella">
            <thead>
              <tr>
                <th>{t('Flusso')}</th>
                <th>{t('Ogni')}</th>
                <th>{t('Prossima')}</th>
                <th>{t('Ultima')}</th>
                <th>{t('Come è andata')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(orari.data?.schedules ?? []).map((o) => (
                <tr key={o.stream}>
                  <td className="cella-titolo">
                    <strong>{o.name}</strong>
                  </td>
                  <td className="num">
                    <select
                      className="input"
                      style={{ width: 130 }}
                      value={o.everyMinutes ?? 60}
                      disabled={!cambiaOrari || cambiaOrario.isPending}
                      onChange={(e) =>
                        cambiaOrario.mutate({
                          stream: o.stream,
                          everyMinutes: Number(e.target.value),
                        })
                      }
                    >
                      {/* una cadenza scritta a database che non è fra quelle
                          proposte (10 minuti) si mostra lo stesso: prima la
                          tendina faceva vedere la prima voce, cioè un'altra */}
                      {[...new Set<number>([...CADENZE, o.everyMinutes ?? 60])]
                        .sort((a, b) => a - b)
                        .map((m) => (
                          <option key={m} value={m}>
                            {cadenza(m)}
                          </option>
                        ))}
                    </select>
                  </td>
                  <td className="num dim">
                    {o.enabled ? (o.nextRunAt ? dataOra(o.nextRunAt) : t('appena possibile')) : '—'}
                  </td>
                  <td className="num dim">{o.lastRunAt ? dataOra(o.lastRunAt) : '—'}</td>
                  <td>
                    {o.lastStatus === null ? (
                      <span className="dim">{t('mai partito')}</span>
                    ) : o.lastStatus === 'succeeded' ? (
                      <span className="badge badge-ok">{t('bene')}</span>
                    ) : o.lastStatus === 'rejected' ? (
                      <span className="badge badge-warn" title={t('Righe scartate: da guardare.')}>
                        {t('con scarti')}
                      </span>
                    ) : (
                      <span className="badge badge-alert">
                        {t('non va')}
                        {o.problemSince && (
                          <span className="dim">
                            {' '}
                            · {t('dal {quando}', { quando: dataOra(o.problemSince) })}
                          </span>
                        )}
                      </span>
                    )}
                  </td>
                  <td className="destra">
                    <button
                      className="btn btn-ghost"
                      disabled={lanciaFlusso.isPending}
                      onClick={() => lanciaFlusso.mutate(o.stream)}
                    >
                      {t('Lancia ora')}
                    </button>
                    {cambiaOrari && (
                      <button
                        className={o.enabled ? 'btn' : 'btn btn-primary'}
                        style={{ marginLeft: 6 }}
                        disabled={cambiaOrario.isPending}
                        onClick={() => cambiaOrario.mutate({ stream: o.stream, enabled: !o.enabled })}
                      >
                        {o.enabled ? t('Spegni') : t('Accendi')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card" style={{ margin: '14px 20px 24px' }}>
        <div className="card-head">
          <span className="card-title">{t('Ultime esecuzioni')}</span>
        </div>
        <div className="table-wrap">
          <table className="tabella">
            <thead>
              <tr>
                <th>{t('Quando')}</th>
                <th>{t('Flusso')}</th>
                <th>{t('Esito')}</th>
                <th className="destra">{t('Lette')}</th>
                <th className="destra">{t('Applicate')}</th>
                <th className="destra">{t('Scartate')}</th>
                <th>{t('Problema')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(esecuzioni.data?.runs ?? []).length === 0 ? (
                <tr>
                  <td colSpan={8} className="dim" style={{ textAlign: 'center', padding: 18 }}>
                    {t('Nessuna esecuzione ancora.')}
                  </td>
                </tr>
              ) : (
                esecuzioni.data!.runs.map((r) => {
                  const s = STATO[r.status] ?? { testo: r.status, classe: '' };
                  const aperta = apertaId === r.id;
                  return (
                    <Fragment key={r.id}>
                      <tr className={aperta ? 'riga-aperta' : undefined}>
                        <td className="num">{dataOra(r.startedAt)}</td>
                        <td>{r.stream}</td>
                        <td>
                          <span className={`badge ${s.classe}`}>{s.testo}</span>
                        </td>
                        <td className="destra num">{r.rowsRead.toLocaleString('it-IT')}</td>
                        <td className="destra num">{r.rowsApplied.toLocaleString('it-IT')}</td>
                        <td className="destra num">
                          {r.rowsRejected > 0 ? (
                            <strong>{r.rowsRejected}</strong>
                          ) : (
                            <span className="dim">—</span>
                          )}
                        </td>
                        <td className="dim" style={{ maxWidth: 320 }}>
                          {r.error ?? ''}
                        </td>
                        <td className="destra">
                          {r.rowsApplied + r.rowsRejected > 0 && (
                            <button
                              className="btn btn-ghost"
                              aria-expanded={aperta}
                              aria-controls={`righe-esecuzione-${r.id}`}
                              onClick={() => setApertaId(aperta ? null : r.id)}
                            >
                              {aperta ? t('Chiudi') : t('Righe')}
                            </button>
                          )}
                        </td>
                      </tr>
                      {/* Le righe si aprono subito sotto la loro esecuzione:
                          prima comparivano in fondo alla tabella, e con
                          cinquanta esecuzioni bisognava andarle a cercare. Si
                          conservano solo quelle che hanno cambiato qualcosa e
                          quelle scartate: sono le uniche che si va a guardare */}
                      {aperta && (
                        <tr className="riga-dettaglio" id={`righe-esecuzione-${r.id}`}>
                          <td colSpan={8}>
                            {(righe.data?.rows ?? []).length === 0 ? (
                              <p className="dim">
                                {righe.isLoading ? t('Un momento…') : t('Nessuna riga conservata.')}
                              </p>
                            ) : (
                              <ul className="doclist">
                                {righe.data!.rows.map((riga) => (
                                  <li key={riga.id}>
                                    <span className="num">{riga.externalKey ?? '—'}</span>
                                    <span className="badge">{riga.action}</span>
                                    <span className="dim">{riga.message ?? ''}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
